import React from 'react';
import { getStats } from '../data/products';

interface AnalyticsDashboardProps {
  queryCount: number;
  avgResponseTime: number;
  successRate: number;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  queryCount,
  avgResponseTime,
  successRate
}) => {
  const stats = getStats();

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200 shadow-lg">
      <h2 className="text-lg font-bold text-purple-900 mb-6 flex items-center gap-2">
        <span className="text-2xl">📊</span>
        Knowledge Base Analytics
      </h2>

      {/* System Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
          <div className="text-sm text-gray-600 mb-1">Total Products</div>
          <div className="text-3xl font-bold text-purple-600">{stats.totalProducts}</div>
          <div className="text-xs text-gray-500 mt-1">Across 4 categories</div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
          <div className="text-sm text-gray-600 mb-1">Total Versions</div>
          <div className="text-3xl font-bold text-indigo-600">{stats.totalVersions}</div>
          <div className="text-xs text-gray-500 mt-1">Version tracking enabled</div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
          <div className="text-sm text-gray-600 mb-1">Documents</div>
          <div className="text-3xl font-bold text-blue-600">{stats.totalDocuments}</div>
          <div className="text-xs text-gray-500 mt-1">100% coverage</div>
        </div>

        <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
          <div className="text-sm text-gray-600 mb-1">Multi-Version</div>
          <div className="text-3xl font-bold text-pink-600">{stats.multiVersionProducts}</div>
          <div className="text-xs text-gray-500 mt-1">Products tracked</div>
        </div>
      </div>

      {/* Query Performance */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span>📈</span> Query Performance (Today)
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Queries</span>
            <span className="text-lg font-bold text-purple-600">{queryCount}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Avg Response Time</span>
            <span className="text-lg font-bold text-indigo-600">{avgResponseTime}s</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Success Rate</span>
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-500"
                  style={{ width: `${successRate}%` }}
                />
              </div>
              <span className="text-lg font-bold text-green-600">{successRate}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span>📂</span> Product Categories
        </h3>
        <div className="space-y-2">
          {Object.entries(stats.categories).map(([category, count]) => (
            <div key={category} className="flex justify-between items-center">
              <span className="text-sm text-gray-600">{category}</span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                {count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Live Status Indicator */}
      <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
        <span>System Active • Last Updated: Just now</span>
      </div>
    </div>
  );
};
